import { getButtonPermission } from './buttonPermissions'

/**
 * Permissions Utility Functions
 * Handles permission mapping and navigation filtering based on user roles
 */

/**
 * Maps role permissions with permission names from permission list
 * @param {Object} userPermissions - User permissions object from login response
 * @param {Array} permissionList - Array of all available permissions
 * @returns {Array} Array of role permissions with permission names added
 */
export const getPermissionNames = (userPermissions, permissionList) => {
  if (!userPermissions || !Array.isArray(permissionList)) return []

  // Convert userPermissions object to array format
  const rolePermissions = Object.entries(userPermissions).map(([permissionId, permissions]) => ({
    permission_id: parseInt(permissionId),
    ...permissions
  }))

  // Get all permission_ids from rolePermissions
  const rolePermissionIds = rolePermissions.map(p => p.permission_id)

  // Filter permissionList to only include items whose id matches rolePermissionIds
  return permissionList
    .filter(permission => {
      const permissionId = permission.permission_id ?? permission.id
      return rolePermissionIds.includes(permissionId)
    })
    .map(permission => {
      // Find the matching role permission
      const matchingRolePermission = rolePermissions.find(
        rolePerm => rolePerm.permission_id === (permission.permission_id ?? permission.id)
      )

      // Return the role permission with permission name added
      return {
        ...matchingRolePermission,
        permission_name: permission.name ?? permission.permission_name
      }
    })
}

/**
 * Filters navigation items based on user permissions
 * @param {Array} navItems - Array of navigation items to filter
 * @param {string} userType - User type (admin, sub_admin, etc.)
 * @param {Array} permissionsWithNames - Array of permissions with names from getPermissionNames
 * @returns {Array} Filtered navigation items
 */
export const getAccessibleNavItems = (navItems, userType, permissionsWithNames) => {
  if (userType === 'admin') {
    return navItems
  }

  const filtered = navItems
    .map(item => {
      // If it's a section title, keep it for now
      if (item.sectionTitle) {
        return { ...item }
      }

      // Find the matching permission using 'name' or 'permission_name'
      const matchedPermission = permissionsWithNames?.find(perm => {
        // Handle both formats: permission_name/name and permission_id/id
        const permName = perm.permission_name || perm.name
        const permId = perm.permission_id ?? perm.id

        // Trim and lowercase for comparison
        const permNameLower = permName?.toLowerCase().trim()
        const itemTitleLower = item.title?.toLowerCase().trim()

        // Check for exact match first
        const exactMatch = permNameLower === itemTitleLower

        // Check if permission name includes item title OR item title includes permission name
        const includesMatch = permNameLower?.includes(itemTitleLower) || itemTitleLower?.includes(permNameLower)

        // Also check if permission_id matches (for cases where title might not match exactly)
        const idMatches = permId && item.permission_id && permId === item.permission_id

        return exactMatch || includesMatch || idMatches
      })

      const updatedItem = { ...item }

      // Add permission_id (fallback to id) if matched
      if (matchedPermission) {
        updatedItem.permission_id = matchedPermission.permission_id ?? matchedPermission.id
      }

      // Recursively check children
      if (item.children) {
        updatedItem.children = getAccessibleNavItems(item.children, userType, permissionsWithNames)
      }

      // Always keep dashboard
      if (item.title === 'Dashboard') {
        return updatedItem
      }

      const hasReadPermission = matchedPermission?.read
      const hasAccessibleChildren = updatedItem.children && updatedItem.children.length > 0

      if (hasReadPermission || hasAccessibleChildren) {
        return updatedItem
      }

      return null
    })
    .filter(Boolean)

  // Second pass: Remove empty sections
  return filtered.filter((item, index) => {
    if (item.sectionTitle) {
      // Check if there are any non-section items following this one before the next section
      for (let i = index + 1; i < filtered.length; i++) {
        if (filtered[i].sectionTitle) return false
        return true // Found a non-section item
      }
      return false // End of array
    }
    return true
  })
}

/**
 * Checks if user has specific permission for a given action
 * @param {Array} rolePermissions - Array of role permissions with permission names
 * @param {string} permissionName - Name of the permission to check
 * @param {string} action - Action to check (read, write, add, remove)
 * @returns {boolean} True if user has permission, false otherwise
 */
export const hasPermission = (rolePermissions, permissionName, action) => {
  const permission = rolePermissions?.find(perm => {
    // Check both permission_name and name fields
    const permName = perm.permission_name || perm.name
    return permName && permName.toLowerCase().includes(permissionName.toLowerCase())
  })

  return permission ? permission[action] : false
}

/**
 * Gets all permissions for a specific permission name
 * @param {Array} rolePermissions - Array of role permissions with permission names
 * @param {string} permissionName - Name of the permission to get
 * @returns {Object|null} Permission object or null if not found
 */
export const getPermissionByName = (rolePermissions, permissionName) => {
  return (
    rolePermissions?.find(perm => {
      // Check both permission_name and name fields
      const permName = perm.permission_name || perm.name
      return permName && permName.toLowerCase().includes(permissionName.toLowerCase())
    }) || null
  )
}

/**
 * Convert user permissions object to array format for easier processing
 * @param {Object} userPermissions - User permissions object from response
 * @returns {Array} Array of permissions with permission_id
 */
export const convertPermissionsToArray = userPermissions => {
  if (!userPermissions || typeof userPermissions !== 'object') return []

  return Object.entries(userPermissions).map(([permissionId, permissions]) => ({
    permission_id: parseInt(permissionId),
    ...permissions
  }))
}

/**
 * Check if user has any permissions
 * @param {Object} userPermissions - User permissions object
 * @returns {boolean} True if user has permissions, false otherwise
 */
export const hasAnyPermissions = userPermissions => {
  return userPermissions && Object.keys(userPermissions).length > 0
}

/**
 * Permission name mapping for common use cases
 * Maps common permission names to actual permission names from API
 */
export const PERMISSION_NAME_MAP = {
  // User Management
  users: 'User Management',
  customers: 'Customer Management',
  businessUsers: 'Business User Management',

  // Product Management
  productCategory: 'Product Category',
  productPackaging: 'Product Packaging',

  // Order Management
  orderService: 'Order Service',
  orderStatus: 'Order Status',
  cashoutRequest: 'Cashout Request',

  // Inventory Management
  inventoryPackage: 'Inventory Package',
  inventoryPackageRequest: 'Inventory Package Request',
  productInventoryRequest: 'Product Inventory Request',

  // System Management
  coupons: 'Coupons',
  referralCode: 'Referral Code',
  cityManagement: 'City Management Access',
  zone: 'Zone Management',
  hqAddress: 'HQ Address Access',

  // Package Management
  packageDimension: 'Package Dimension',
  packageWeight: 'Package Weight',
  packageOpenedCharges: 'Package Opened Charges',

  // Issue Management
  logIssues: 'Log Issues',
  mainReasons: 'Main Reasons',
  commissionAmount: 'Commission Amount',

  // Billing
  billingInvoicePayment: 'Billing Invoice Payment',

  // Role Management
  subAdmin: 'Sub Admin',
  roles: 'Roles',
  permissions: 'Permissions',

  // Website Content
  privacyPolicy: 'Privacy Policy',
  termsConditions: 'Terms Conditions',
  testimonials: 'Testimonials',
  contactUs: 'Contact Us',
  faqs: 'FAQs',
  blog: 'Blog',
  ourTeam: 'Our Team'
}

/**
 * Get the actual permission name from a common name
 * @param {string} commonName - Common permission name
 * @returns {string} Actual permission name or the common name if not found
 */
export const getActualPermissionName = commonName => {
  return PERMISSION_NAME_MAP[commonName] || commonName
}

/**
 * Enhanced hasPermission function that supports common permission names
 * @param {Array} rolePermissions - Array of role permissions
 * @param {string} permissionName - Common permission name or actual permission name
 * @param {string} action - Action to check (read, write, add, remove)
 * @returns {boolean} True if user has permission, false otherwise
 */
export const hasPermissionEnhanced = (rolePermissions, permissionName, action) => {
  // First try with the original permission name
  let hasPerm = hasPermission(rolePermissions, permissionName, action)

  if (hasPerm) return true

  // If not found, try with the mapped permission name
  const actualPermissionName = getActualPermissionName(permissionName)
  if (actualPermissionName !== permissionName) {
    hasPerm = hasPermission(rolePermissions, actualPermissionName, action)
  }

  return hasPerm
}

/**
 * Checks if the Actions column should be shown based on user permissions
 * @param {string} userType - User type (admin, sub_admin)
 * @param {Array} permissionsWithNames - Array of permissions with names
 * @param {string} pageType - Page type for permission mapping
 * @param {Array} actions - Array of actions to check (default: ['edit', 'delete'])
 * @param {boolean} hasOtherActions - Whether there are other always-visible actions
 * @returns {boolean} True if the column should be shown
 */
export const canShowActionColumn = (
  userType,
  permissionsWithNames,
  pageType,
  actions = ['edit', 'delete'],
  hasOtherActions = false
) => {
  if (userType === 'admin' || hasOtherActions) {
    return true
  }

  return actions.some(action => {
    const permissionName = getButtonPermission(pageType, action)
    if (!permissionName) return false

    // Check if permission_name matches item title
    const actionKey = action === 'add' ? 'add' : action === 'edit' ? 'write' : 'remove'

    return hasPermissionEnhanced(permissionsWithNames, permissionName, actionKey)
  })
}
